import { useCallback, useMemo, useRef, useState } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CUSTOMER_SEARCH_DEBOUNCE_MS,
  CUSTOMER_SEARCH_MIN_LENGTH,
} from 'constants/customerSelectSearch';

import { Option } from 'types';
import { CustomerData } from 'types/api/customer.type';

import { customersDataService } from 'services';

import { Label } from 'components/Label';
import {
  CODE_SELECT_MENU_MAX_WIDTH,
  codeSelectComponents,
  formatCodeOptionLabel,
} from 'components/OrderAddressBlocks';

import * as S from '../../Order.styles';

type OrderCustomerFieldProps = Readonly<{
  control: Control<FieldValues>;
  /** When true: shipped order or order already has line items — show a read-only code box. */
  readOnly: boolean;
  readOnlyDisplay: string;
  /** Zone for `GET /customers/search` (order zone, then fallbacks from parent). */
  searchZone: string;
  customerInfo?: CustomerData;
  /** Draft customer when picking another user from search (no PUT until blur saves). `null` when selection matches persisted `savedCustomerCode`. */
  onDraftCustomerCode: (customerCode: string | null) => void;
  /** Persisted customer on the order — used to clear draft when user re-selects current customer. */
  savedCustomerCode?: string;
  /**
   * The customer picked and not saved yet. While there is one the field shows it, even though the page still
   * holds the saved customer's details until the pick's own details have loaded: without this the field would
   * flip back to the old customer for that moment (and a blur then would save a customer the field does not show).
   */
  draftCustomerCode?: string | null;
  /** Optional: persist draft customer (PUT) when the field loses focus. */
  onBlur?: () => void;
  /** Block expanded: the select shows the open-book icon; collapsed: no icon. */
  expanded?: boolean;
}>;

export function OrderCustomerField({
  control,
  readOnly,
  readOnlyDisplay,
  searchZone,
  customerInfo,
  onDraftCustomerCode,
  savedCustomerCode = '',
  draftCustomerCode = null,
  onBlur,
  expanded = false,
}: OrderCustomerFieldProps) {
  const { t } = useTranslation();
  const [customerOptions, setCustomerOptions] = useState<Option[]>([]);
  const [pickedOption, setPickedOption] = useState<Option | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fromDetails = useMemo<Option | null>(() => {
    if (!customerInfo?.code) return null;
    return {
      value: customerInfo.code,
      label: `${customerInfo.code} - ${customerInfo.companyName}`,
    };
  }, [customerInfo?.code, customerInfo?.companyName]);

  const selectedOption = pickedOption ?? fromDetails;

  // The picked option and the customer the order really has (fromDetails) are always in the list. Without the
  // second one, a refused change leaves the field blank: the draft is cleared, the form value goes back to the
  // saved customer, but `pickedOption` is still the refused one and the saved customer is in no option.
  const effectiveOptions = useMemo(() => {
    const pinned = [selectedOption, fromDetails].filter(
      (o, i, all): o is Option =>
        o !== null && all.findIndex((x) => x?.value === o.value) === i
    );
    const missing = pinned.filter((p) => !customerOptions.some((o) => o.value === p.value));
    return missing.length === 0 ? customerOptions : [...missing, ...customerOptions];
  }, [selectedOption, fromDetails, customerOptions]);

  const handleCustomerInputChange = useCallback(
    (inputValue: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (!searchZone || inputValue.length < CUSTOMER_SEARCH_MIN_LENGTH) {
        setCustomerOptions([]);
        return;
      }

      debounceTimer.current = setTimeout(() => {
        // Only approved customers: the backend refuses an order for any other.
        customersDataService
          .searchCustomers(inputValue, searchZone, undefined, undefined, 'APPROVED', true)
          .then((res) => {
            const mapped =
              res.data.content?.map((item) => ({
                value: item.customerCode,
                label: `${item.customerCode} - ${item.displayName}`,
              })) ?? [];
            setCustomerOptions(mapped);
          })
          .catch(() => {
            setCustomerOptions([]);
          });
      }, CUSTOMER_SEARCH_DEBOUNCE_MS);
    },
    [searchZone]
  );

  const handleCustomerSelect = useCallback((selected: Option) => {
    setPickedOption(selected?.value ? selected : null);
  }, []);

  if (readOnly) {
    return (
      <S.ReadOnlyCodeField data-testid="customer-readonly">
        <Label htmlFor="customerCode">{t('customer.label')}</Label>
        <S.CodeBox>{readOnlyDisplay || '—'}</S.CodeBox>
      </S.ReadOnlyCodeField>
    );
  }

  return (
    <S.Select
      control={control}
      name="customerCode"
      label={t('customer.label')}
      options={effectiveOptions}
      defaultValue={draftCustomerCode || customerInfo?.code}
      disabled={!searchZone}
      onInputChange={handleCustomerInputChange}
      onSelect={(opt) => {
        handleCustomerSelect(opt);
        if (!opt?.value) {
          onDraftCustomerCode(null);
          return;
        }
        const code = String(opt.value);
        onDraftCustomerCode(code === savedCustomerCode ? null : code);
      }}
      onBlur={onBlur}
      motive="filter"
      data-testid="select-form"
      placeholder={t('common.typeAndSelect')}
      emptyOptionLabel={t('common.selectPlaceholder')}
      formatOptionLabel={formatCodeOptionLabel}
      components={codeSelectComponents(expanded)}
      menuMaxWidth={CODE_SELECT_MENU_MAX_WIDTH}
    />
  );
}
